(() => {
  const revealSelectors = [
    '.cw-intro__grid','.cw-section-head','.cw-explore__grid','.cw-feature__media','.cw-feature__copy',
    '.cw-collection-card','.cw-standard__top','.cw-standard__grid','.cw-project',
    '.cw-trade__copy','.cw-trade__media','.cw-process-grid','.cw-cta .cw-shell'
  ];
  const headerControllers = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let activeMenu = null;
  let featureImageRequest = 0;
  const initReveal = (root = document) => {
    const items = [...root.querySelectorAll(revealSelectors.join(','))].filter(el => !el.dataset.cwRevealReady);
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || reducedMotion.matches) {
      items.forEach(el => { el.classList.add('cw-reveal','visible'); el.dataset.cwRevealReady = 'true'; });
      return;
    }
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      }
    }), { threshold: .12 });
    items.forEach(el => { el.classList.add('cw-reveal'); el.dataset.cwRevealReady = 'true'; observer.observe(el); });
  };
  const initHeader = (root = document) => {
    root.querySelectorAll('[data-cw-header]').forEach(header => {
      if (headerControllers.has(header)) return;
      const events = new AbortController();
      const signal = events.signal;
      const trigger = header.querySelector('[data-cw-open-menu]');
      const drawer = trigger && document.getElementById(trigger.getAttribute('aria-controls'));
      const backdrop = drawer && document.getElementById(drawer.id + '-backdrop');
      const groups = [...header.querySelectorAll('[data-cw-nav-group]')];
      const desktop = window.matchMedia('(min-width: 1301px)');
      let isOpen = false;
      let closingTimer;
      let openingFrame;
      let previousFocus;
      let previousOverflow = '';
      const setSubmenu = (group, expanded) => {
        const toggle = group.querySelector('[data-cw-submenu-toggle]');
        const panel = group.querySelector('[data-cw-submenu]');
        if (!toggle || !panel) return;
        toggle.setAttribute('aria-expanded', String(expanded));
        panel.hidden = !expanded;
      };
      const closeSubmenus = (except = null) => groups.forEach(group => {
        if (group !== except) setSubmenu(group, false);
      });
      const focusableItems = () => [...drawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')]
        .filter(el => !el.closest('[hidden],[inert]') && el.getClientRects().length);
      const hideDrawer = () => {
        drawer.hidden = true;
        if (backdrop) backdrop.hidden = true;
      };
      const closeMenu = ({ restoreFocus = true, immediate = false } = {}) => {
        if (!drawer || !isOpen) return;
        isOpen = false;
        if (activeMenu && activeMenu.drawer === drawer) activeMenu = null;
        clearTimeout(closingTimer);
        cancelAnimationFrame(openingFrame);
        trigger.setAttribute('aria-expanded', 'false');
        drawer.classList.remove('open');
        drawer.inert = true;
        drawer.setAttribute('aria-hidden', 'true');
        if (backdrop) backdrop.classList.remove('open');
        document.body.style.overflow = previousOverflow;
        if (restoreFocus) {
          const target = previousFocus && previousFocus.isConnected ? previousFocus : trigger;
          target.focus({ preventScroll: true });
        }
        if (immediate || reducedMotion.matches) hideDrawer();
        else closingTimer = window.setTimeout(hideDrawer, 320);
      };
      const openMenu = () => {
        if (!drawer || isOpen || desktop.matches) return;
        if (activeMenu) activeMenu.close({ restoreFocus: false, immediate: true });
        closeSubmenus();
        clearTimeout(closingTimer);
        previousFocus = trigger;
        previousOverflow = document.body.style.overflow;
        isOpen = true;
        activeMenu = { drawer, close: closeMenu };
        drawer.hidden = false;
        drawer.inert = false;
        drawer.setAttribute('aria-hidden', 'false');
        trigger.setAttribute('aria-expanded', 'true');
        if (backdrop) backdrop.hidden = false;
        document.body.style.overflow = 'hidden';
        openingFrame = requestAnimationFrame(() => {
          openingFrame = requestAnimationFrame(() => {
            if (!isOpen) return;
            drawer.classList.add('open');
            if (backdrop) backdrop.classList.add('open');
          });
        });
        (drawer.querySelector('[data-cw-close-menu]') || drawer).focus({ preventScroll: true });
      };
      const updateScroll = () => header.classList.toggle('scrolled', window.scrollY > 10);
      updateScroll();
      window.addEventListener('scroll', updateScroll, { passive: true, signal });
      groups.forEach(group => {
        const toggle = group.querySelector('[data-cw-submenu-toggle]');
        if (!toggle) return;
        toggle.hidden = false;
        toggle.addEventListener('click', () => {
          const expanded = toggle.getAttribute('aria-expanded') !== 'true';
          closeSubmenus(group);
          setSubmenu(group, expanded);
        }, { signal });
        toggle.addEventListener('keydown', event => {
          if (event.key !== 'ArrowDown') return;
          event.preventDefault();
          closeSubmenus(group);
          setSubmenu(group, true);
          group.querySelector('[data-cw-submenu] a')?.focus();
        }, { signal });
        group.addEventListener('focusout', () => {
          window.setTimeout(() => {
            if (!group.contains(document.activeElement)) setSubmenu(group, false);
          }, 0);
        }, { signal });
      });
      document.addEventListener('click', event => {
        groups.forEach(group => {
          if (!group.contains(event.target)) setSubmenu(group, false);
        });
      }, { signal });
      document.addEventListener('keydown', event => {
        if (isOpen && event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          closeMenu();
          return;
        }
        if (isOpen && event.key === 'Tab') {
          const items = focusableItems();
          if (!items.length) { event.preventDefault(); drawer.focus(); return; }
          const first = items[0];
          const last = items[items.length - 1];
          if (event.shiftKey && (document.activeElement === first || document.activeElement === drawer)) {
            event.preventDefault(); last.focus();
          } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === drawer)) {
            event.preventDefault(); first.focus();
          }
          return;
        }
        if (event.key === 'Escape') {
          const expandedGroup = groups.find(group => group.querySelector('[data-cw-submenu-toggle][aria-expanded="true"]'));
          if (expandedGroup) {
            event.preventDefault();
            setSubmenu(expandedGroup, false);
            expandedGroup.querySelector('[data-cw-submenu-toggle]').focus();
          }
        }
      }, { signal });
      if (trigger && drawer) {
        trigger.hidden = false;
        trigger.setAttribute('aria-expanded', 'false');
        drawer.hidden = true;
        drawer.inert = true;
        drawer.setAttribute('aria-hidden', 'true');
        trigger.addEventListener('click', () => isOpen ? closeMenu() : openMenu(), { signal });
        drawer.addEventListener('click', event => {
          if (event.target.closest('[data-cw-close-menu],a[href]')) closeMenu();
        }, { signal });
        backdrop?.addEventListener('click', () => closeMenu(), { signal });
        document.addEventListener('focusin', event => {
          if (isOpen && !drawer.contains(event.target)) (focusableItems()[0] || drawer).focus();
        }, { signal });
        desktop.addEventListener('change', event => {
          closeSubmenus();
          if (!event.matches || !isOpen) return;
          const focusWasInside = drawer.contains(document.activeElement);
          closeMenu({ restoreFocus: false, immediate: true });
          if (focusWasInside) header.querySelector('.cw-brand')?.focus();
        }, { signal });
      }
      header.dataset.cwHeaderReady = 'true';
      headerControllers.set(header, () => {
        closeMenu({ restoreFocus: false, immediate: true });
        clearTimeout(closingTimer);
        cancelAnimationFrame(openingFrame);
        events.abort();
        delete header.dataset.cwHeaderReady;
      });
    });
  };
  const initFeatures = (root = document) => root.querySelectorAll('[data-cw-feature] [data-cw-swatch]').forEach(swatch => {
    swatch.setAttribute('aria-pressed', String(swatch.classList.contains('active')));
  });
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const swatch = event.target.closest('[data-cw-swatch]');
    const feature = swatch?.closest('[data-cw-feature]');
    if (!feature) return;
    const image = feature.querySelector('[data-cw-feature-image]');
    const request = String(++featureImageRequest);
    feature.dataset.cwImageRequest = request;
    feature.setAttribute('aria-busy', 'true');
    const applySelection = () => {
      if (!feature.isConnected || feature.dataset.cwImageRequest !== request) return;
      feature.querySelectorAll('[data-cw-swatch]').forEach(item => {
        const selected = item === swatch;
        item.classList.toggle('active', selected);
        item.setAttribute('aria-pressed', String(selected));
      });
      [['title','[data-cw-title]'],['type','[data-cw-type]'],['desc','[data-cw-desc]'],['title','[data-cw-caption]']].forEach(([key, selector]) => {
        const target = feature.querySelector(selector);
        if (target && swatch.hasAttribute('data-' + key)) target.textContent = swatch.dataset[key];
      });
      const link = feature.querySelector('[data-cw-feature-link]');
      if (link && swatch.dataset.url) link.href = swatch.dataset.url;
      const frame = feature.querySelector('[data-cw-sample-frame]');
      if (frame) frame.setAttribute('style', swatch.dataset.sampleStyle || '--cw-sample-ratio:0.5;');
      if (image) {
        if (swatch.dataset.srcset) image.setAttribute('srcset', swatch.dataset.srcset);
        else image.removeAttribute('srcset');
        if (swatch.dataset.image) image.src = swatch.dataset.image;
        if (swatch.hasAttribute('data-alt')) image.alt = swatch.dataset.alt;
      }
      feature.removeAttribute('aria-busy');
    };
    if (image && swatch.dataset.image) {
      const preload = new Image();
      preload.onload = applySelection;
      preload.onerror = () => {
        if (feature.dataset.cwImageRequest === request) feature.removeAttribute('aria-busy');
      };
      preload.sizes = image.sizes;
      if (swatch.dataset.srcset) preload.srcset = swatch.dataset.srcset;
      preload.src = swatch.dataset.image;
    } else applySelection();
  });
  const init = (root = document) => { initReveal(root); initHeader(root); initFeatures(root); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('castlewood:content-ready', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-cw-header]').forEach(header => {
      headerControllers.get(header)?.();
      headerControllers.delete(header);
    });
  });
})();

